<template>
  <form>
    <div class="category-top-bar sticky-header">
      <div class="top-bar">
        <p>
          <a class="btn btn-orange btn-icon back" :title="$t('general.back')" @click.prevent="backButton">
            <i class="ti-arrow-left"></i>
            {{ $t('general.back') }}
          </a>
        </p>
        <p class="d-inline-flex">
          <a class="btn btn-danger btn-icon btn mar-rt-10 delete" v-if="id" @click.prevent="deleteCampaign">
            <img src="/static/img/icons/icon-close.png">
            {{ $t('general.delete') }}
          </a>
          <button type="submit" class="btn btn-success btn-icon mar-rt-10 save" @click.prevent="validate" :title="$t('general.save')">
            <img src="/static/img/icons/save.png">
            {{ $t('general.save') }}
          </button>
        </p>
      </div>
    </div>

    <div class="row mar-to-15 mar-bt-15 raffle-campaign-form">
      <div class="col-md-12">
        <div class="card raffle-campaign-form__card">
          <div :class="(loading ? 'show-loader' : 'hide-loader')">
            <div class="el-loading-spinner">
              <svg viewBox="25 25 50 50" class="circular">
                <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
              </svg>
            </div>
          </div>
          <div class="card-content">
            <div class="header raffle-campaign-form__header">
              <h5>{{ $t('raffleCampaigns.details') }}</h5>
            </div>
            <p class="text-danger" v-if="errors.has('error')">{{ getError('error') }}</p>

            <div class="row">
              <div class="col-lg-6 col-md-12">
                <div class="form-group">
                  <label>{{ $t('raffleCampaigns.name') }}</label>
                  <input type="text"
                         name="name"
                         :placeholder="$t('raffleCampaigns.name')"
                         v-model="model.name"
                         v-validate="modelValidations.name"
                         class="form-control">
                  <small class="text-danger" v-if="errors.has('name')">{{ getError('name') }}</small>
                </div>

                <div class="form-group">
                  <label>{{ $t('raffleCampaigns.description') }}</label>
                  <textarea
                    rows="4"
                    name="description"
                    :placeholder="$t('raffleCampaigns.description')"
                    v-model="model.description"
                    class="form-control">
                  </textarea>
                </div>

                <div class="form-group">
                  <label>{{ $t('raffleCampaigns.winnersDescription') }}</label>
                  <textarea
                    rows="4"
                    name="winners_description"
                    :placeholder="$t('raffleCampaigns.winnersPlaceholder')"
                    v-model="model.winners_description"
                    class="form-control">
                  </textarea>
                </div>

                <div class="form-group">
                  <label>{{ $t('raffleCampaigns.type') }}</label>
                  <el-select
                    v-model="model.type"
                    style="width: 100%;"
                    :disabled="!!id"
                    :placeholder="$t('raffleCampaigns.type')">
                    <el-option
                      v-for="option in typeOptions"
                      :key="option.value"
                      :label="option.label"
                      :value="option.value">
                    </el-option>
                  </el-select>
                  <small v-if="id" class="text-muted d-block">{{ $t('raffleCampaigns.typeLocked') }}</small>
                  <small v-if="model.type === 'order_count'" class="text-muted d-block">{{ $t('raffleCampaigns.typeOrderCountHint') }}</small>
                  <small v-else-if="model.type === 'order_total'" class="text-muted d-block">{{ $t('raffleCampaigns.typeOrderTotalHint') }}</small>
                  <small class="text-danger" v-if="errors.has('type')">{{ getError('type') }}</small>
                </div>

                <div class="form-group" v-if="model.type === 'order_count'">
                  <label>{{ $t('raffleCampaigns.minOrderValue') }}</label>
                  <input type="number"
                         name="min_order_value"
                         min="0"
                         step="0.01"
                         :placeholder="$t('raffleCampaigns.minOrderValue')"
                         v-model="model.min_order_value"
                         class="form-control">
                  <small class="text-danger" v-if="errors.has('min_order_value')">{{ getError('min_order_value') }}</small>
                </div>
              </div>

              <div class="col-lg-6 col-md-12">
                <div class="form-group">
                  <label>{{ $t('raffleCampaigns.image') }}</label>
                  <el-upload
                    v-if="!loading"
                    action=""
                    ref="upload"
                    :limit="2"
                    :file-list="model.imagelist"
                    list-type="picture-card"
                    accept=".jpg, .jpeg, .png"
                    :on-change="handleImgChange"
                    :on-exceed="handleExceed"
                    :on-remove="handleRemove"
                    :auto-upload="false">
                    <img src="/static/img/icons/icon-plus-primary.png">
                    <div class="el-upload__tip" slot="tip">{{ $t('validations.pictureSize') }}{{ $t('validations.imageResolution') }}</div>
                  </el-upload>
                  <small class="text-danger" v-if="errors.has('image')">{{ getError('image') }}</small>
                </div>

                <div class="form-group">
                  <label>{{ $t('raffleCampaigns.pdf') }}</label>
                  <el-upload
                    v-if="!loading"
                    action=""
                    ref="pdfUpload"
                    :limit="1"
                    :file-list="model.pdflist"
                    accept=".pdf"
                    :on-change="handlePdfChange"
                    :on-exceed="handlePdfExceed"
                    :on-remove="handlePdfRemove"
                    :auto-upload="false">
                    <el-button size="small" type="primary">{{ $t('upload.browse') }}</el-button>
                    <div class="el-upload__tip" slot="tip">{{ $t('validations.pdfSize') }}</div>
                  </el-upload>
                  <small class="text-danger" v-if="errors.has('pdf')">{{ getError('pdf') }}</small>
                </div>

                <div class="form-group">
                  <label>{{ $t('raffleCampaigns.startEnd') }}</label>
                  <el-date-picker
                    type="datetimerange"
                    v-model="model.fromto"
                    format="yyyy-MM-dd HH:mm:ss"
                    value-format="yyyy-MM-dd HH:mm:ss"
                    style="width: 100%;">
                  </el-date-picker>
                  <small class="text-danger" v-if="errors.has('starts_at')">{{ getError('starts_at') }}</small>
                  <small class="text-danger" v-if="errors.has('ends_at')">{{ getError('ends_at') }}</small>
                </div>

                <div class="form-group">
                  <label>{{ $t('raffleCampaigns.active') }}</label>
                  <p-switch v-model="model.active" type="success"></p-switch>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import Vue from 'vue'
import { Input, Upload, Button, DatePicker, Select, Option } from 'element-ui'
import PSwitch from 'src/components/UIComponents/Switch.vue'
import swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.css'
import moment from 'moment'

Vue.use(DatePicker)
Vue.use(Upload)
Vue.use(Button)
Vue.use(Input)
Vue.use(Select)

export default {
  components: {
    [Button.name]: Button,
    [Input.name]: Input,
    [Select.name]: Select,
    [Option.name]: Option,
    PSwitch
  },
  props: {
    route: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      id: this.route.params.id || null,
      loading: true,
      model: {
        name: '',
        description: '',
        winners_description: '',
        type: 'order_count',
        min_order_value: null,
        fromto: [],
        active: true,
        imagelist: [],
        image: null,
        pdflist: [],
        pdf: null
      },
      modelValidations: {
        name: {
          required: true,
          min: 3
        }
      }
    }
  },
  computed: {
    typeOptions () {
      return [
        {
          value: 'order_count',
          label: this.$t('raffleCampaigns.typeOrderCount')
        },
        {
          value: 'order_total',
          label: this.$t('raffleCampaigns.typeOrderTotal')
        }
      ]
    }
  },
  created () {
    if (this.id) {
      this.getCampaign()
    } else {
      this.loading = false
    }
  },
  methods: {
    setRaffleCampaignImagelist (item) {
      if (item.image_url) {
        item.imagelist = item.image_url
      } else if (item.image) {
        item.imagelist = [{ url: item.image }]
      } else {
        item.imagelist = []
      }
      if (!Array.isArray(item.imagelist)) {
        item.imagelist = item.imagelist ? [{ url: String(item.imagelist) }] : []
      }
    },
    setRaffleCampaignPdflist (item) {
      if (item.pdf_url) {
        item.pdflist = [{ name: 'document.pdf', url: item.pdf_url }]
      } else if (item.pdf) {
        const pdfUrl = typeof item.pdf === 'string' ? item.pdf : ''
        item.pdflist = pdfUrl ? [{ name: 'document.pdf', url: pdfUrl }] : []
      } else {
        item.pdflist = []
      }
      if (!Array.isArray(item.pdflist)) {
        item.pdflist = item.pdflist ? [{ name: 'document.pdf', url: String(item.pdflist) }] : []
      }
    },
    parseCampaignDateForPicker (value) {
      if (!value) return null
      return moment(String(value)).format('YYYY-MM-DD HH:mm:ss')
    },
    getCampaign () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/raffle-campaigns/' + this.id, 'GET', data, (response) => {
        if (response.status === true) {
          const item = (response.data && response.data[0]) ? response.data[0] : response.data
          if (item.starts_at && item.ends_at) {
            item.fromto = [
              this.parseCampaignDateForPicker(item.starts_at),
              this.parseCampaignDateForPicker(item.ends_at)
            ]
          } else {
            item.fromto = []
          }
          this.setRaffleCampaignImagelist(item)
          this.setRaffleCampaignPdflist(item)
          item.active = !!(parseInt(item.active) === 1 || item.active === true)
          if (!item.type) {
            item.type = 'order_count'
          }
          if (item.min_order_value === undefined || item.min_order_value === null) {
            item.min_order_value = null
          }
          if (item.winners_description == null) {
            item.winners_description = ''
          }
          this.model = Object.assign({}, this.model, item)
        }
        this.$nextTick(() => {
          this.loading = false
        })
      })
    },
    getError (fieldName) {
      return this.errors.first(fieldName)
    },
    validate () {
      this.$validator.validateAll().then(isValid => {
        if (!isValid) return
        if (!this.model.fromto || !this.model.fromto[0] || !this.model.fromto[1]) {
          this.errors.add('starts_at', this.$t('raffleCampaigns.periodRequired'))
          return
        }
        if (!this.id && !this.model.type) {
          this.errors.add('type', this.$t('raffleCampaigns.typeRequired'))
          return
        }
        this.errors.clear()
        this.saveCampaign()
      })
    },
    handleErrors (errors) {
      for (const prop in errors) {
        if (Object.prototype.hasOwnProperty.call(errors, prop)) {
          if (Array.isArray(errors[prop]) && errors[prop].length === 1) {
            this.errors.add(prop, errors[prop][0])
          } else {
            this.errors.add(prop, errors[prop])
          }
        }
      }
    },
    saveCampaign () {
      this.loading = true
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        name: this.model.name,
        description: this.model.description || '',
        winners_description: this.model.winners_description || '',
        active: this.model.active ? 1 : 0,
        starts_at: this.model.fromto[0],
        ends_at: this.model.fromto[1]
      }

      if (!this.id) {
        data.type = this.model.type
      }

      if (this.model.type === 'order_count') {
        data.min_order_value = this.model.min_order_value !== null && this.model.min_order_value !== ''
          ? this.model.min_order_value
          : null
      }

      if (this.model.image) {
        data.image = this.model.image
      } else if (Array.isArray(this.model.imagelist) && this.model.imagelist.length === 0) {
        data.image = ''
      }

      if (this.model.pdf && String(this.model.pdf).indexOf('data:') === 0) {
        data.pdf = this.model.pdf
      } else if (Array.isArray(this.model.pdflist) && this.model.pdflist.length === 0) {
        data.pdf = ''
      }

      const url = this.id ? '/raffle-campaigns/' + this.id : '/raffle-campaigns'
      const httpMethod = this.id ? 'PUT' : 'POST'

      this.apiRequest(url, httpMethod, data, (response) => {
        this.loading = false
        if (response.status === true) {
          swal({
            title: '',
            text: this.id ? this.$t('raffleCampaigns.campaignUpdated') : this.$t('raffleCampaigns.campaignAdded'),
            type: 'success',
            showConfirmButton: false,
            timer: 1500
          }).catch(() => {})
          this.backButton()
        } else if (response.errors) {
          this.handleErrors(response.errors)
        } else {
          swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
        }
      })
    },
    deleteCampaign () {
      let btnCounter = 3
      swal({
        text: this.$t('raffleCampaigns.areYouSureDelete') + ' ' + (this.model.name || '') + '?',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes') + ' (' + btnCounter + ')'
      }).then((result) => {
        if (result) {
          const data = {
            token: this.$session.get('token'),
            company_id: this.$session.get('selectedCompany')
          }
          this.apiRequest('/raffle-campaigns/' + this.id, 'DELETE', data, (response) => {
            if (response.status === true) {
              swal('', this.$t('raffleCampaigns.campaignDeleted'), 'success').catch(() => {})
              this.backButton()
            } else {
              swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
            }
          })
        }
      }).catch(() => {})
      swal.disableConfirmButton()
      const btnInterval = setInterval(() => {
        btnCounter--
        if (btnCounter >= 1) {
          swal.getConfirmButton().textContent = this.$t('general.yes') + ' (' + btnCounter + ')'
        } else {
          clearInterval(btnInterval)
          swal.getConfirmButton().textContent = this.$t('general.yes')
          swal.enableConfirmButton()
        }
      }, 1000)
    },
    handleImgChange (file, fileList) {
      const isOkSize = file.size / 1024 / 1024 < 5
      if (!isOkSize) {
        swal({
          toast: true,
          type: 'warning',
          text: this.$t('validations.pictureSizeExceed'),
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        })
        this.$refs.upload.clearFiles()
        this.model.imagelist = []
        return
      }
      if (fileList.length > 1) {
        this.model.imagelist = fileList.slice(1)
      } else {
        this.model.imagelist = fileList
      }
      if (this.model.imagelist && this.model.imagelist[0] && this.model.imagelist[0].status === 'ready') {
        this.getBase64(this.model.imagelist[0].raw).then((base64enc) => {
          this.model.image = base64enc
        })
      }
    },
    handleRemove (file, fileList) {
      if (fileList.length === 0) {
        this.model.imagelist = []
        this.model.image = null
      }
    },
    handleExceed () {
      swal({
        text: this.$t('raffleCampaigns.removeCurrentImage'),
        type: 'warning',
        showConfirmButton: true
      })
    },
    handlePdfChange (file, fileList) {
      const isOkSize = file.size / 1024 / 1024 < 25
      const isPdf = file.raw && file.raw.type === 'application/pdf'
      if (!isOkSize) {
        swal({
          toast: true,
          type: 'warning',
          text: this.$t('validations.pdfSizeExceed'),
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        })
        this.$refs.pdfUpload.clearFiles()
        this.model.pdflist = []
        return
      }
      if (!isPdf) {
        swal({
          toast: true,
          type: 'warning',
          text: this.$t('validations.wrongFormat'),
          position: 'top-end',
          showConfirmButton: true,
          timer: 3000
        })
        this.$refs.pdfUpload.clearFiles()
        this.model.pdflist = []
        return
      }
      this.model.pdflist = fileList.slice(-1)
      if (this.model.pdflist[0] && this.model.pdflist[0].status === 'ready') {
        this.getBase64(this.model.pdflist[0].raw).then((base64enc) => {
          this.model.pdf = base64enc
        })
      }
    },
    handlePdfRemove (file, fileList) {
      if (fileList.length === 0) {
        this.model.pdflist = []
        this.model.pdf = null
      }
    },
    handlePdfExceed () {
      swal({
        text: this.$t('raffleCampaigns.removeCurrentPdf'),
        type: 'warning',
        showConfirmButton: true
      })
    },
    backButton () {
      this.$bus.$emit('refresh')
      this.$bus.$emit('close-modal')
    }
  }
}
</script>

<style scoped>
.raffle-campaign-form__card {
  height: 100%;
  width: 100%;
  margin: 0;
}

.raffle-campaign-form__header {
  margin: -15px 0 30px !important;
}

.el-date-editor {
  width: 100% !important;
}

.content {
  padding: 0 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/RaffleCampaigns/RaffleCampaignForm.vue